/**
 * Shared HTTP error helper used by codegen-emitted query functions AND
 * the hand-written `createMutation` factory.
 *
 * Why: the backend returns a canonical error envelope
 *   { error: { code, numericId, message, details }, requestId }
 * (see api-health-harness). Previously SDK call sites threw a generic
 *   `Error("GET /v1/x failed: 404")`
 * which discards every useful debugging field.
 *
 * This helper:
 *   - reads the response body and parses the canonical envelope
 *   - maps the HTTP status to an SdkError category+code (file 05 taxonomy)
 *   - threads the server-echoed correlationId through (so Better Stack joins
 *     browser → REST → DB → indexer logs by a single id)
 *   - attaches every raw HTTP debug field as own properties so
 *     `serializeForTelemetry` in the frontend flattens them into
 *     searchable `error_*` columns on Better Stack
 *
 * The frontend's `normalize-error.ts` already enumerates
 * `Object.getOwnPropertyNames`, so any field we Object.assign here lands as
 * an `error_<field>` column without further wiring.
 */

import { resolveCorrelationId } from '../errors/correlation.js';
import { SdkError } from '../errors/SdkError.js';
import type {
  AuthCode,
  RateCode,
  SdkErrorCategory,
  ServerCode,
  ValidationCode,
} from '../types/SdkError.js';

const BODY_TRUNCATE_LIMIT = 800;

interface ParsedEnvelope {
  readonly apiCode: string | undefined;
  readonly apiMessage: string | undefined;
  readonly details: unknown;
  readonly fieldErrors: Readonly<Record<string, string>> | undefined;
}

function parseEnvelope(bodyText: string | undefined): ParsedEnvelope {
  if (typeof bodyText !== 'string' || bodyText.length === 0) {
    return {
      apiCode: undefined,
      apiMessage: undefined,
      details: undefined,
      fieldErrors: undefined,
    };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText) as unknown;
  } catch {
    return {
      apiCode: undefined,
      apiMessage: undefined,
      details: undefined,
      fieldErrors: undefined,
    };
  }
  if (parsed === null || typeof parsed !== 'object' || !('error' in parsed)) {
    return {
      apiCode: undefined,
      apiMessage: undefined,
      details: undefined,
      fieldErrors: undefined,
    };
  }
  const errObj = (parsed as { error: unknown }).error;
  if (errObj === null || typeof errObj !== 'object') {
    return {
      apiCode: undefined,
      apiMessage: undefined,
      details: undefined,
      fieldErrors: undefined,
    };
  }
  const e = errObj as Record<string, unknown>;
  const apiCode = typeof e.code === 'string' ? e.code : undefined;
  const apiMessage = typeof e.message === 'string' ? e.message : undefined;
  const details = 'details' in e ? e.details : undefined;
  const fieldErrors = extractFieldErrors(details);
  return { apiCode, apiMessage, details, fieldErrors };
}

/**
 * Best-effort: if `details` is a flat `{ [field]: string | string[] }` map,
 * surface it as `fieldErrors` for the validation variant. Returns undefined
 * when details is not a flat string map (in which case `details` itself is
 * preserved verbatim on the error instance).
 */
function extractFieldErrors(details: unknown): Readonly<Record<string, string>> | undefined {
  if (details === null || typeof details !== 'object' || Array.isArray(details)) return undefined;
  const out: Record<string, string> = {};
  let any = false;
  for (const [k, v] of Object.entries(details as Record<string, unknown>)) {
    if (typeof v === 'string') {
      out[k] = v;
      any = true;
    } else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      out[k] = (v as string[]).join('; ');
      any = true;
    }
  }
  return any ? out : undefined;
}

function parseRetryAfterMs(headers: Headers): number {
  const raw = headers.get('retry-after');
  if (raw === null) return 0;
  if (/^\d+$/.test(raw.trim())) {
    const milliseconds = Number(raw) * 1000;
    return Number.isFinite(milliseconds) ? milliseconds : 0;
  }
  const asDate = Date.parse(raw);
  if (Number.isFinite(asDate)) {
    const ms = asDate - Date.now();
    return ms > 0 ? ms : 0;
  }
  return 0;
}

interface CategoryAndCode {
  readonly category: SdkErrorCategory;
  readonly code: string;
}

/**
 * Map HTTP status → SdkError category+code (file 05 taxonomy).
 *
 *   400, 422              → validation
 *   401, 403              → auth
 *   429                   → rate-limited
 *   404, 408-410, 412 etc → server (httpStatus: number is unconstrained)
 *   5xx                   → server (specific code where the status implies it)
 *
 * The taxonomy doesn't have a dedicated "not-found" bucket — 404 lands on
 * `server` because the server returned a normal response that happens to
 * be "no such resource". `apiCode` (backend `error.code`) is preserved
 * verbatim on the instance so dashboards can still group by it.
 */
function categoryAndCodeFor(status: number): CategoryAndCode {
  if (status === 400)
    return { category: 'validation', code: 'BAD_REQUEST' satisfies ValidationCode };
  if (status === 422)
    return { category: 'validation', code: 'UNPROCESSABLE' satisfies ValidationCode };
  if (status === 401) return { category: 'auth', code: 'UNAUTHORIZED' satisfies AuthCode };
  if (status === 403) return { category: 'auth', code: 'FORBIDDEN' satisfies AuthCode };
  if (status === 429)
    return { category: 'rate-limited', code: 'TOO_MANY_REQUESTS' satisfies RateCode };
  if (status === 502) return { category: 'server', code: 'BAD_GATEWAY' satisfies ServerCode };
  if (status === 503)
    return { category: 'server', code: 'SERVICE_UNAVAILABLE' satisfies ServerCode };
  return { category: 'server', code: 'INTERNAL' satisfies ServerCode };
}

/**
 * Build a short i18n-style user message key. The actual translation lookup
 * is the consumer's job; this is the stable identifier the SDK promises.
 */
function userMessageFor(category: SdkErrorCategory, status: number): string {
  if (status === 404) return 'errors.server.not_found';
  if (category === 'auth') return `errors.auth.${status === 401 ? 'unauthorized' : 'forbidden'}`;
  if (category === 'validation') return 'errors.validation.bad_request';
  if (category === 'rate-limited') return 'errors.rate_limited.too_many_requests';
  if (category === 'server') return 'errors.server.unavailable';
  return 'errors.unknown';
}

export interface HttpErrorDebugFields {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly method: string;
  readonly requestId: string | undefined;
  readonly apiCode: string | undefined;
  readonly apiMessage: string | undefined;
  readonly details: unknown;
  readonly body: string | undefined;
}

export async function throwHttpError(method: string, url: string, res: Response): Promise<never> {
  const requestId = res.headers.get('x-request-id') ?? undefined;
  const correlationFromHeader = res.headers.get('x-correlation-id') ?? requestId;

  let bodyText: string | undefined;
  try {
    bodyText = await res.text();
  } catch {
    bodyText = undefined;
  }

  const { apiCode, apiMessage, details, fieldErrors } = parseEnvelope(bodyText);

  const { category, code } = categoryAndCodeFor(res.status);

  const codePart = apiCode !== undefined ? ` [${apiCode}]` : '';
  const reqIdPart = requestId !== undefined ? ` (requestId ${requestId})` : '';
  const msgPart = apiMessage !== undefined ? `: ${apiMessage}` : '';
  let bodyPart = '';
  if (
    apiCode === undefined &&
    apiMessage === undefined &&
    typeof bodyText === 'string' &&
    bodyText.length > 0
  ) {
    const truncated =
      bodyText.length > BODY_TRUNCATE_LIMIT
        ? `${bodyText.slice(0, BODY_TRUNCATE_LIMIT)}…`
        : bodyText;
    bodyPart = `\nbody: ${truncated}`;
  }
  const devMessage = `${method} ${url} → ${res.status} ${res.statusText}${codePart}${reqIdPart}${msgPart}${bodyPart}`;

  const init: Record<string, unknown> = {
    category,
    code,
    userMessage: userMessageFor(category, res.status),
    devMessage,
    correlationId: resolveCorrelationId(correlationFromHeader),
    httpStatus: res.status,
  };
  if (category === 'rate-limited') {
    init.retryAfterMs = parseRetryAfterMs(res.headers);
  }
  if (category === 'validation') {
    init.fieldErrors = fieldErrors ?? {};
  }

  const sdkErr = new SdkError(init as ConstructorParameters<typeof SdkError>[0]);

  const debug: HttpErrorDebugFields = {
    status: res.status,
    statusText: res.statusText,
    url,
    method,
    requestId,
    apiCode,
    apiMessage,
    details,
    body: bodyText,
  };
  Object.assign(sdkErr, debug, { retryAfterMs: parseRetryAfterMs(res.headers) });

  throw sdkErr;
}
